import type { ViewMode } from '@mona-roadmap/domain';
import { gridTemplate, type ColumnDef, type ColumnKey } from './columns';
import { readJson, writeJson } from './storage';

/**
 * The user's column layout for one view: the order of the columns and any widths they dragged. Persisted per view in
 * localStorage. The Goal / Epic column is pinned first (it is sticky and holds the tree), so it is never reordered.
 */
export interface ColumnPrefs {
  order: ColumnKey[];
  widths: Partial<Record<ColumnKey, number>>;
}

export const PINNED: ColumnKey = 'tree';
export const MIN_WIDTH = 64;
export const MAX_WIDTH = 900;

export const emptyPrefs = (): ColumnPrefs => ({ order: [], widths: {} });
export const isCustomized = (p: ColumnPrefs): boolean => p.order.length > 0 || Object.keys(p.widths).length > 0;
export const clampWidth = (px: number): number => Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, px)));

/** Accepts whatever was stored and keeps only what is well formed (storage can be stale, edited or from another version). */
export function sanitizePrefs(raw: unknown): ColumnPrefs {
  const out = emptyPrefs();
  if (!raw || typeof raw !== 'object') return out;
  const { order, widths } = raw as { order?: unknown; widths?: unknown };
  if (Array.isArray(order)) out.order = [...new Set(order.filter((k): k is ColumnKey => typeof k === 'string'))];
  if (widths && typeof widths === 'object') {
    for (const [k, v] of Object.entries(widths)) if (typeof v === 'number' && Number.isFinite(v)) out.widths[k as ColumnKey] = clampWidth(v);
  }
  return out;
}

/**
 * The view's columns in the saved order. Columns missing from the saved order (a column added later, or one that only
 * exists in this view) go right after the column that precedes them by default; saved keys that no longer exist are dropped.
 */
export function applyOrder(defaults: ColumnDef[], order: ColumnKey[]): ColumnDef[] {
  const byKey = new Map(defaults.map((c) => [c.key, c]));
  const keys: ColumnKey[] = order.filter((k) => k !== PINNED && byKey.has(k));
  defaults.forEach((c, i) => {
    if (c.key === PINNED || keys.includes(c.key)) return;
    const before = defaults.slice(0, i).reverse().find((d) => d.key !== PINNED && keys.includes(d.key));
    keys.splice(before ? keys.indexOf(before.key) + 1 : 0, 0, c.key);
  });
  const pinned = byKey.get(PINNED);
  return [...(pinned ? [pinned] : []), ...keys.map((k) => byKey.get(k)!)];
}

/** Moves `key` to where `target` is now (so dragging right puts it after the target, dragging left puts it before). */
export function moveColumn(order: ColumnKey[], key: ColumnKey, target: ColumnKey): ColumnKey[] {
  const from = order.indexOf(key);
  if (key === PINNED || from < 0 || key === target) return order;
  const next = order.filter((k) => k !== key);
  const at = target === PINNED ? 1 : order.indexOf(target);
  next.splice(Math.min(Math.max(at, 1), next.length), 0, key);
  return next;
}

/** grid-template-columns: the dragged width where there is one, otherwise the column's default track. */
export const templateFor = (cols: ColumnDef[], widths: ColumnPrefs['widths']): string => gridTemplate(cols.map((c) => (widths[c.key] ? { ...c, width: `${widths[c.key]}px` } : c)));

export const columnPrefsStorageKey = (view: ViewMode): string => `mona.columns.${view}`;
export const loadColumnPrefs = (view: ViewMode): ColumnPrefs => sanitizePrefs(readJson<unknown>(columnPrefsStorageKey(view), null));
export const saveColumnPrefs = (view: ViewMode, p: ColumnPrefs): void => writeJson(columnPrefsStorageKey(view), p);
